<template>
  <b-container fluid>
    <b-row>
      <b-col class="p-2">
        <esg-base-chart height="300px">
          <loading :active="loading"
                   :z-index="1000"
                   :is-full-page="false"/>
          <apex-chart ref="chart"
                      type="line"
                      height="300px"
                      v-show="!loading"
                      :options="chartOptions"
                      :series="series"/>
        </esg-base-chart>
      </b-col>
    </b-row>

    <div v-show="!loading">
      <div class="table-title">온실가스 배출권 구매 비용</div>
      <esg-table-without-meta :fields="fields"
                              :items="items.slice(0, 2)"
                              v-show="!loading"
                              size="xs">
        <template #cell(type)="{ value, item }">
          <td class="text-left"
              :style="{ cursor: tooltips[item.name] ? 'pointer' : 'normal' }"
              v-b-tooltip.hover
              :title="tooltips[item.name]">{{ value }}
          </td>
        </template>
        <template v-for="year in years"
                  v-slot:[`cell(${year})`]="{ value, item }">
          <td v-if="year === firstYear"
              :key="year"
              class="text-right"> {{ formatValues(value, item) }}
          </td>
          <td v-else
              :key="year"
              class="text-right">
            <span class="d-inline-block mr-1">{{ formatValues(value, item) }}</span>
            <span class="d-inline-block text-left"
                  :style="{ color: color(item, year), width: '60px' }">
              {{ formatChangeRate(item, year) }}
          </span>
          </td>
        </template>
      </esg-table-without-meta>

      <div class="table-title mt-4">
        온실가스 할당 및 배출량
      </div>
      <esg-table-without-meta :fields="fields"
                              :items="items.slice(2)"
                              v-show="!loading"
                              size="xs">
        <template #cell(type)="{ value, item }">
          <td class="text-left"
              :style="{ cursor: tooltips[item.name] ? 'pointer' : 'normal' }"
              v-b-tooltip.hover
              :title="tooltips[item.name]">{{ value }}
          </td>
        </template>
        <template v-for="year in years"
                  v-slot:[`cell(${year})`]="{ value, item }">
          <td v-if="year === firstYear"
              :key="year"
              class="text-right"> {{ formatValues(value, item) }}
          </td>
          <td v-else
              :key="year"
              class="text-right">
            <span class="d-inline-block mr-1">{{ formatValues(value, item) }}</span>
            <span class="d-inline-block text-left"
                  :style="{ color: color(item, year), width: '60px' }">
              {{ formatChangeRate(item, year) }}
          </span>
          </td>
        </template>
      </esg-table-without-meta>
    </div>
  </b-container>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";

export default {
  name: 'expectedPurchaseCost',
  components: {
    EsgTableWithoutMeta,
    EsgBaseChart,
  },
  created() {
    this.fetchData();
  },
  props: {
    firstYear: {
      type: Number,
      default: 2018
    },
    lastYear: {
      type: Number,
      default: 2050
    }
  },
  watch: {
    firstYear: {
      immediate: true,
      handler() {
        this.fetchData();
      }
    },
    lastYear: {
      immediate: true,
      handler() {
        this.fetchData();
      }
    }
  },
  computed: {
    red() {
      return this.colors[3];
    },
    blue() {
      return this.colors[7];
    },
    colors() {
      return this.$store.getters['ghg/colors'];
    },
    years() {
      return Array.from({ length: this.lastYear - this.firstYear + 1 }, (_, i) => this.firstYear + i);
    }
  },
  data() {
    return {
      loading: false,
      tooltips: {
        kauPrice: null,
        expectedKauPurchaseCost: '④ 잉여/부족 배출량 x ① KAU 배출권 판매가격',
        totalQuota: null,
        emissionTotal: null,
        balance: '③ 총할당량 – ⑤ Scope 1+2 배출량'
      },
      items: [],
      fields: [],
      series: [],
      chartOptions: {
        chart: {
          zoom: {
            enabled: false
          },
          toolbar: {
            show: false
          }
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [1],
          offsetY: -5,
          formatter: val => this.formatNumber(val / 10e6, 0),
          style: {
            colors: ['#000']
          }
        },
        stroke: {
          width: 2,
          show: true,
          curve: 'straight',
        },
        grid: {
          show: true,
          padding: {
            right: 20,
          }
        },
        labels: [],
        yaxis: {
          title: {
            text: '예상 KAU 매입 비용 (백만원)',
            style: {
              fontSize: '12px',
              fontWeight: 'normal'
            }
          },
          labels: {
            formatter: val => this.formatNumber(val / 10e6, 0)
          }
        },
        xaxis: {
          type: 'datetime',
          crosshairs: { show: false },
          tooltip: {
            enabled: false,
          }
        },
        tooltip: {
          x: {
            formatter: val => new Date(val).getFullYear() + '년'
          },
          y: {
            formatter: val => this.formatNumber(val / 10e6, 0) + ' 백만원'
          }
        },
        markers: {
          size: 4,
          strokeWidth: 3,
          shape: "circle",
          hover: {
            sizeOffset: 2
          }
        },
      },
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        let args = [{ years: this.years }]

        let params = new FormData()
        params.append('path', 'esg/kau_purchase_cost/main')
        params.append('json', JSON.stringify(args))

        let response = await axios.post('esg/lca/run/python/chart-data', params, {
          headers: { Authorization: accessToken() },
        });

        if (!response.data.data) return;
        let list = JSON.parse(response.data.data)[0].data;

        this.setSeries(list);
        this.setLabels(list);
        this.setItems(list);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setSeries(list) {
      let expectedKauPurchaseCost = list.map(row => row.expected_kau_purchase_cost);

      // Calculate cumulative sums
      let cumsum = 0;
      let cumsums = [];
      for (let value of expectedKauPurchaseCost) {
        cumsum += value;
        cumsums.push(cumsum);
      }

      this.series = [
        { name: '예상 KAU 매입 비용', data: expectedKauPurchaseCost },
        { name: '누계', data: cumsums }
      ]
    },
    setLabels(list) {
      this.chartOptions.labels = list.map(row => `${row.year}-01-01`)
    },
    setItems(list) {
      const years = [...new Set(list.map(row => row.year))];
      let kauPrice = { name: 'kauPrice', type: '① KAU 판매가격 (단위: 백만원)' };
      let expectedKauPurchaseCost = { name: 'expectedKauPurchaseCost', type: '② KAU 매입 예상비용 (단위: 백만원)' };
      let totalQuota = { name: 'totalQuota', type: '③ 총할당량 (단위: tCO2)' };
      let balance = { name: 'balance', type: '④ 잉여/부족 배출량 (단위: tCO2)' };
      let emissionTotal = { name: 'emissionTotal', type: '⑤ Scope 1 + 2 배출량 (단위: tCO2)' };
      const items = [kauPrice, expectedKauPurchaseCost, totalQuota, balance, emissionTotal];

      this.fields = [{ name: 'type', label: '항목', list: true }]
      for (let year of years) {
        const row = list.find(row => row.year === year);
        const yearStr = year.toString();
        kauPrice[yearStr] = row.kau_price;
        expectedKauPurchaseCost[yearStr] = row.expected_kau_purchase_cost / 10e6;
        totalQuota[yearStr] = row.carbon_emission_total_quota;
        balance[yearStr] = row.carbon_emission_balance;
        emissionTotal[yearStr] = row.carbon_emission_scope1_and_2;
        this.fields.push({
          name: yearStr,
          label: yearStr,
          list: true,
        })
      }
      this.items = items;
    },
    formatValues(value, item) {
      if (item.name === 'kauPrice') {
        return this.formatNumber(value / 10e6, 7, 7);
      } else {
        return this.formatNumber(value, 0);
      }
    },
    color(item, year) {
      let value = this.changeRate(item, year);
      return value > 0 ? this.red : (value < 0 ? this.blue : '#000');
    },
    changeRate(item, year) {
      const currYear = year.toString();
      const prevYear = (year - 1).toString();

      const curr = item[currYear];
      const prev = item[prevYear];
      return 100 * (curr - prev) / Math.abs(prev);
    },
    formatChangeRate(item, year) {
      const change = this.changeRate(item, year);
      const arrow = change > 0 ? '▲' : (change < 0 ? '▼' : ' - ');
      const formattedChange = change === 0 ? '' : this.formatNumber(Math.abs(change), 1) + '%';
      return `(${arrow} ${formattedChange})`
    },
  }
}
</script>

<style scoped>
.table-title {
  font-weight: bolder;
}
</style>